"use client";

import { useState, useCallback, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  Settings,
  Shield,
  FileCode,
  Sliders,
  TestTube,
  Loader2,
  Save,
} from "lucide-react";
import { GeneralTab } from "./general-tab";
import { AuthTab } from "./auth-tab";
import { HeadersTab } from "./headers-tab";
import { AdvancedTab } from "./advanced-tab";
import { TestTab } from "./test-tab";
import { getAuthTokenAsync } from "@/lib/auth";
import { normalizeBackendUrl } from "@/lib/api-config";
import { useRouter } from "next/navigation";
import type { ConnectionConfig } from "../connection-studio";

interface ConnectionBuilderProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
  /** Called after a successful save — parent should close the create panel and refresh */
  onSaved?: (connectionId: string) => void;
  /** Called when user clicks Cancel — parent should close the create panel */
  onCancel?: () => void;
}

export function ConnectionBuilder({
  formData,
  onChange,
  onSaved,
  onCancel,
}: ConnectionBuilderProps) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("general");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Tracks the connection ID once it has been persisted (auto-save or manual save)
  const connectionIdRef = useRef<string | null>(null);

  // Generate next version of a name (e.g., "My Connection" -> "My Connection-1" -> "My Connection-2")
  const getNextVersionName = (name: string): string => {
    const versionMatch = name.match(/^(.+)-(\d+)$/);
    if (versionMatch) {
      const baseName = versionMatch[1];
      const version = parseInt(versionMatch[2], 10);
      return `${baseName}-${version + 1}`;
    }
    return `${name}-1`;
  };

  /**
   * Create or update the connection and return the connection ID.
   * When called silently (silent=true) it won't redirect or show the
   * global saving spinner — used by auto-save on spec upload.
   * `configOverrides` lets callers pass freshly-parsed data (e.g. from a
   * spec) that hasn't propagated through React state yet.
   */
  const saveConnection = useCallback(
    async (opts?: {
      nameOverride?: string;
      silent?: boolean;
      configOverrides?: Partial<ConnectionConfig>;
    }): Promise<string | null> => {
      const { nameOverride, silent = false, configOverrides } = opts ?? {};

      // Merge any overrides the caller provides (handles async state lag)
      const merged = configOverrides
        ? { ...formData, ...configOverrides }
        : formData;

      const connectionName = nameOverride || merged.name;

      // If already auto-saved AND this is a silent call, return existing ID
      // For non-silent (manual save button), update the connection with latest data and redirect
      if (connectionIdRef.current && silent) {
        return connectionIdRef.current;
      }

      const needsBaseUrl = !["database", "file_share"].includes(merged.type);
      if (!connectionName || (needsBaseUrl && !merged.base_url)) {
        if (!silent) setSaveError(needsBaseUrl ? "Please fill in the required fields (Name and Base URL)" : "Please fill in the connection name");
        return null;
      }

      if (!silent) {
        setIsSaving(true);
        setSaveError(null);
      }

      try {
        const authToken = await getAuthTokenAsync();
        const API_URL = normalizeBackendUrl(
          process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_BACKEND_URL,
        );

        // If connection was already auto-saved, update it with latest form data
        if (connectionIdRef.current) {
          const updateResponse = await fetch(
            `${API_URL}/v1/connections/${connectionIdRef.current}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({
                name: connectionName,
                description: merged.description,
                type: merged.type,
                auth_type: merged.auth_type,
                auth_config: merged.auth_config,
                config: {
                  base_url: merged.base_url,
                  headers: merged.headers,
                  ...merged.config,
                },
              }),
            },
          );

          if (!updateResponse.ok) {
            const errorData = await updateResponse.json();
            throw new Error(errorData.detail || "Failed to update connection");
          }

          if (onSaved) {
            onSaved(connectionIdRef.current);
          } else {
            router.push("/connection-studio");
          }
          return connectionIdRef.current;
        }

        const response = await fetch(`${API_URL}/v1/connections/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            name: connectionName,
            description: merged.description,
            type: merged.type,
            auth_type: merged.auth_type,
            auth_config: merged.auth_config,
            config: {
              base_url: merged.base_url,
              headers: merged.headers,
              ...merged.config,
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          const errorDetail = errorData.detail || "Failed to create connection";

          // Check for duplicate name error - auto-increment and retry
          if (
            errorDetail.includes("duplicate key") &&
            errorDetail.includes("ng_connections_name")
          ) {
            const newName = getNextVersionName(connectionName);
            onChange({ name: newName });
            const versionMatch = newName.match(/-(\d+)$/);
            const version = versionMatch ? parseInt(versionMatch[1], 10) : 1;
            if (version <= 10) {
              if (!silent) setIsSaving(false);
              return saveConnection({ nameOverride: newName, silent, configOverrides });
            }
            throw new Error(
              `Could not find a unique name after 10 attempts. Please manually rename.`,
            );
          }

          throw new Error(errorDetail);
        }

        const data = await response.json();
        connectionIdRef.current = data.id;

        if (!silent) {
          if (onSaved) {
            onSaved(data.id);
          } else {
            router.push("/connection-studio");
          }
        }

        return data.id as string;
      } catch (error) {
        console.error("Save error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save connection";
        if (!silent) setSaveError(errorMessage);
        return null;
      } finally {
        if (!silent) setIsSaving(false);
      }
    },
    [formData, onChange, onSaved, router],
  );

  /** Wraps saveConnection for the manual "Save" button (with redirect). */
  const handleSaveConnection = async () => {
    await saveConnection();
  };

  const typesWithoutBaseUrl = ["database", "file_share"];
  const isFormValid =
    formData.name.trim().length > 0 &&
    (typesWithoutBaseUrl.includes(formData.type) || formData.base_url.trim().length > 0);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="h-auto w-full justify-start gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1">
          <TabsTrigger
            value="general"
            className={cn(
              "h-8 rounded-lg px-3 text-[12px] font-medium text-white/60",
              "data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 data-[state=active]:shadow-sm data-[state=active]:shadow-teal-500/10",
              "hover:bg-white/[0.04] hover:text-white/55",
            )}
          >
            <Settings className="mr-1.5 h-3.5 w-3.5" />
            General
          </TabsTrigger>
          <TabsTrigger
            value="authentication"
            className={cn(
              "h-8 rounded-lg px-3 text-[12px] font-medium text-white/60",
              "data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 data-[state=active]:shadow-sm data-[state=active]:shadow-teal-500/10",
              "hover:bg-white/[0.04] hover:text-white/55",
            )}
          >
            <Shield className="mr-1.5 h-3.5 w-3.5" />
            Auth
          </TabsTrigger>
          <TabsTrigger
            value="headers"
            className={cn(
              "h-8 rounded-lg px-3 text-[12px] font-medium text-white/60",
              "data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 data-[state=active]:shadow-sm data-[state=active]:shadow-teal-500/10",
              "hover:bg-white/[0.04] hover:text-white/55",
            )}
          >
            <FileCode className="mr-1.5 h-3.5 w-3.5" />
            Headers
          </TabsTrigger>
          <TabsTrigger
            value="advanced"
            className={cn(
              "h-8 rounded-lg px-3 text-[12px] font-medium text-white/60",
              "data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 data-[state=active]:shadow-sm data-[state=active]:shadow-teal-500/10",
              "hover:bg-white/[0.04] hover:text-white/55",
            )}
          >
            <Sliders className="mr-1.5 h-3.5 w-3.5" />
            Advanced
          </TabsTrigger>
          <TabsTrigger
            value="test"
            className={cn(
              "h-8 rounded-lg px-3 text-[12px] font-medium text-white/60",
              "data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 data-[state=active]:shadow-sm data-[state=active]:shadow-teal-500/10",
              "hover:bg-white/[0.04] hover:text-white/55",
            )}
          >
            <TestTube className="mr-1.5 h-3.5 w-3.5" />
            Test
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="general" className="mt-0">
            <GeneralTab
              formData={formData}
              onChange={onChange}
              onEnsureConnection={saveConnection}
            />
          </TabsContent>

          <TabsContent value="authentication" className="mt-0">
            <AuthTab formData={formData} onChange={onChange} />
          </TabsContent>

          <TabsContent value="headers" className="mt-0">
            <HeadersTab formData={formData} onChange={onChange} />
          </TabsContent>

          <TabsContent value="advanced" className="mt-0">
            <AdvancedTab formData={formData} onChange={onChange} />
          </TabsContent>

          <TabsContent value="test" className="mt-0">
            <TestTab formData={formData} />
          </TabsContent>
        </div>
      </Tabs>

      {/* Save Button */}
      <div className="flex items-center justify-between border-t border-white/[0.06] pt-6">
        <div>
          {saveError && (
            <p className="text-[12px] text-rose-400/70">{saveError}</p>
          )}
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                router.push("/connection-studio");
              }
            }}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-2 text-[12px] font-medium text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white/60"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveConnection}
            disabled={!isFormValid || isSaving}
            className="flex items-center gap-2 rounded-lg bg-teal-500/90 px-4 py-2 text-[12px] font-semibold text-white shadow-sm shadow-teal-500/15 transition-all hover:bg-teal-500 hover:shadow-teal-500/25 disabled:opacity-30"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5" />
                Save Connection
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
